import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { collection, addDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import ImageUpload from '../../components/ui/ImageUpload';

const CreateTeamPage = () => {
  const [teamName, setTeamName] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [teamLogo, setTeamLogo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch available players (not assigned to any team)
    const playersQuery = query(
      collection(db, 'players'),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(playersQuery, (snapshot) => {
      const players = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Filter out players already assigned to teams
      const unassignedPlayers = players.filter(player => !player.teamId);
      setAvailablePlayers(unassignedPlayers);
    });

    return unsubscribe;
  }, []);

  const handleAddPlayer = (player) => {
    if (selectedPlayers.find(p => p.id === player.id)) {
      setError('Player already selected');
      return;
    }
    setSelectedPlayers([...selectedPlayers, player]);
    setError('');
  };

  const handleRemovePlayer = (playerId) => {
    setSelectedPlayers(selectedPlayers.filter(p => p.id !== playerId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
        const teamData = {
          name: teamName,
          logo: teamLogo,
          leagueId: null, // Will be set when assigned to a league
          players: selectedPlayers.map(p => ({
            id: p.id,
            name: p.name,
            selected: true // Mark as selected for team matches
          })),
          playerIds: selectedPlayers.map(p => p.id),
          matchesPlayed: 0,
          wins: 0,
          losses: 0,
          points: 0,
          createdAt: new Date().toISOString()
        };

      // Add team to Firestore
      const teamRef = await addDoc(collection(db, 'teams'), teamData);

      // Update players with team assignment
      // Note: In a real app, you'd use batch writes to update players
      console.log('Team created:', teamRef.id);
      
      navigate('/teams');
    } catch (error) {
      setError('Failed to create team. Please try again.');
      console.error('Error creating team:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/teams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Teams
        </button>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Create New Team
        </h1>
        <p className="text-gray-600">
          Add a new team to the league
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Team Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Team Details
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <label htmlFor="teamName" className="form-label">
                    Team Name *
                  </label>
                  <input
                    id="teamName"
                    type="text"
                    required
                    className="form-input"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter team name"
                  />
                </div>

                <div className="form-group">
                  <ImageUpload
                    currentImageUrl={teamLogo}
                    onImageChange={setTeamLogo}
                    folder="team-logos"
                    aspectRatio="square"
                    className="mb-4"
                  />
                </div>

            <div className="form-group">
              <label className="form-label">
                Selected Players ({selectedPlayers.length}) <span className="text-sm text-gray-500">(Optional - can add later)</span>
              </label>
              {selectedPlayers.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedPlayers.map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <span className="font-medium">{player.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePlayer(player.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  No players selected yet (You can create the team and add players later)
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
              style={{
                backgroundColor: loading ? '#999' : '#1C563D',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating Team...' : 'Create Team'}
            </button>
          </form>
        </motion.div>

        {/* Available Players */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Available Players
          </h2>
          
          {availablePlayers.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availablePlayers.map((player) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium">{player.name}</div>
                    <div className="text-sm text-gray-600">
                      {player.email || 'No email provided'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddPlayer(player)}
                    disabled={selectedPlayers.find(p => p.id === player.id)}
                    className="btn btn-sm btn-primary"
                    style={{
                      backgroundColor: selectedPlayers.find(p => p.id === player.id) ? '#999' : '#1C563D',
                      cursor: selectedPlayers.find(p => p.id === player.id) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No available players found</p>
              <p className="text-sm mt-2">
                <a href="/players/create" style={{ color: '#1C563D' }}>
                  Create players first
                </a>
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CreateTeamPage;
